// TechDayCard (round 2, pages/board-tech-menu.md §1): a technician's day so far, live, on top of their
// menu on the board. It answers three questions without leaving the board: where are they and what are
// they doing now; how long have they worked today, and will today's bookings take them past the daily
// overtime line; is their truck ready for what's next. Nothing in it repeats the lane (no visits).
// The site they're at has its own line and presses through to that visit. Its minutes are the timesheet's (time.team's per-person totals, the same words and the same format),
// so the two never disagree. Minutes only, never pay. A problem is drawn as one: red words with the red
// rule beside them (DESIGN.md §2); everything else is plain. Live from the board's stream, it says when
// it's as of; offline it says so and goes still. Reads only --techday-*.
import { Fragment } from 'react';
import { PenKey } from '../PenKey/PenKey.tsx';
import type { Pen } from '../pen.ts';
import './TechDayCard.css';

/** What they're doing now, from their open time entry. */
export type TechNow =
  | { kind: 'working'; address: string; since: number; visitId?: string }
  | { kind: 'driving'; since: number }
  /** A non-job entry: `words` is how it reads ("On a break", "At the shop", "At the supplier"). */
  | { kind: 'nonJob'; words: string; since: number }
  | { kind: 'notClockedIn' }
  | { kind: 'ended'; at: number };

export interface TechTruck {
  /** "Truck 3": their stock location (stock_locations, one per technician by staff_id). */
  name: string;
  /** Items below their minimum on the truck. */
  low: number;
  /** Stock requests waiting. */
  requests: number;
  /** Today's picklist: ready (built and loaded), or not yet. */
  picklist?: 'ready' | 'notBuilt' | 'notLoaded';
}

/** One technician's day so far: the shape of the team's-day read (time.team's per-person row, today). */
export interface TechDay {
  name: string;
  pen?: Pen | null;
  now: TechNow;
  /** Their first clock-in today, in minutes from midnight, or null. */
  clockIn: number | null;
  /** Paid minutes so far by type, corrections included: time.team's totals. */
  totals: { work: number; drive: number; nonJob: number };
  /** Ended entries flagged for payroll, not yet settled. */
  flagged: number;
  /** Entries still running today (0 or 1). */
  runningNow: number;
  /** Entries left open on an earlier day: a missed clock-out. */
  leftOpen: number;
  /** The day of the newest one left open, in words ("Fri Oct 23"). */
  leftOpenOn?: string;
  /** When their last booked visit today ends, in minutes from midnight, or null when none is left. */
  bookedUntil?: number | null;
  /** Their truck, or null for a technician without one. */
  truck?: TechTruck | null;
}

export type TechDayStatus = 'live' | 'offline' | 'loading' | 'failed';

export interface TechDayCardProps {
  day: TechDay;
  /** When the numbers are from, in minutes from midnight. */
  asOf: number;
  status?: TechDayStatus;
  /** `now` for a CSR: who they are and what they're doing, nothing more (the spec's who-sees-what). */
  detail?: 'full' | 'now';
  /** Missed clock-outs and flagged entries: not for a CSR. */
  showProblems?: boolean;
  /**
   * The problems in the timesheet's own words, each its own red line (problemCardWords). Without it, the
   * card says them from the counts (leftOpen, flagged).
   */
  problems?: readonly string[];
  /** Under the problems, in ink: who fixes them, for a viewer who can't (problemsNoteWords). */
  problemsNote?: string | null;
  /** The company's daily overtime line, in paid minutes (British Columbia: 8 h). */
  overtimeAfter?: number;
  /** Pressing the address: that visit, selected on the board. */
  onSelectVisit?: (visitId: string) => void;
  onRetry?: () => void;
}

/** 7:12 a.m., 12 p.m., 4:30 p.m. */
export function clockWord(minute: number): string {
  const m = ((Math.round(minute) % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  const h12 = h % 12 || 12;
  return `${h12}${mm ? `:${String(mm).padStart(2, '0')}` : ''} ${h < 12 ? 'a.m.' : 'p.m.'}`;
}

/** The timesheet's minutes: 135 is "2 h 15 min", 0 is "0 min" (apps/web timesheet-model minutesText). */
export function minutesWords(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (!h) return `${rest} min`;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface OvertimeAhead {
  words: string;
  /** Booked past the line: a problem. Otherwise close but fine. */
  bad: boolean;
}

/**
 * Will today's bookings take them past the daily overtime line? Said whenever they're on the clock (the
 * timesheet design: the 8 h line ahead is always there while running): when they'd reach it (now plus
 * what's left of the line, rounded to 5 min) against when their bookings end. Booked past it: a problem,
 * in red. Otherwise plain. Not running: nothing (null).
 */
export function overtimeAhead(day: TechDay, asOf: number, after = 480): OvertimeAhead | null {
  if (!day.runningNow) return null;
  const paid = day.totals.work + day.totals.drive + day.totals.nonJob;
  const line = minutesWords(after);
  const until = day.bookedUntil ?? null;
  if (paid >= after) {
    if (until === null || until <= asOf)
      return { words: `Past ${line} already, nothing else booked`, bad: false };
    return { words: `Past ${line} already, booked until ${clockWord(until)}`, bad: true };
  }
  const reaches = Math.round((asOf + after - paid) / 5) * 5;
  if (until !== null && until > reaches)
    return {
      words: `Reaches ${line} about ${clockWord(reaches)}, booked until ${clockWord(until)}`,
      bad: true,
    };
  return {
    words:
      until !== null && until > asOf
        ? `Reaches ${line} about ${clockWord(reaches)}, booked until ${clockWord(until)}`
        : `Reaches ${line} about ${clockWord(reaches)}, nothing else booked`,
    bad: false,
  };
}

/** The truck's line, or null when there's nothing to say (stocked) or no truck. */
export function truckWords(truck: TechTruck | null | undefined): string | null {
  if (!truck) return null;
  const parts = [
    truck.low ? count(truck.low, 'item low', 'items low') : null,
    truck.requests ? count(truck.requests, 'request waiting', 'requests waiting') : null,
    truck.picklist === 'notBuilt' ? 'today’s picklist not built' : null,
    truck.picklist === 'notLoaded' ? 'today’s picklist not loaded' : null,
  ].filter(Boolean);
  return parts.length ? `${truck.name}: ${parts.join(', ')}` : null;
}

// ── Time problems, in the timesheet's own words (pages/timesheet-design rules.md §7, §10), shared by the
// board's lane head, this card and the timesheet. A day is a company date, 2026-10-23. ──

/** What's wrong with an earlier day's time: a clock-out never tapped, two of them, or one to check. */
export type TimeProblemKind = 'noClockOut' | 'twoClockOuts' | 'checkTime';

export interface TimeProblem {
  kind: TimeProblemKind;
  /** The company's day it's on: "2026-10-23". */
  date: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-23" is "Fri Oct 23": a calendar date, so no time zone comes into it. */
export function dayWord(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const at = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[at.getUTCDay()]} ${MONTHS[m - 1]} ${d}`;
}

/** The card's words, the full date: "Clock-out missing for Fri Oct 23". */
export function problemCardWords(p: TimeProblem): string {
  const day = dayWord(p.date);
  if (p.kind === 'noClockOut') return `Clock-out missing for ${day}`;
  if (p.kind === 'twoClockOuts') return `Two clock-outs on ${day}`;
  return `Time to check on ${day}`;
}

/** The lane head's words: the names column is narrow, so no weekday ("No clock-out Oct 23"). */
export function problemLaneWords(p: TimeProblem): string {
  const day = dayWord(p.date).slice(4);
  if (p.kind === 'noClockOut') return `No clock-out ${day}`;
  if (p.kind === 'twoClockOuts') return `2 clock-outs ${day}`;
  return `Check time ${day}`;
}

/**
 * For someone who can't fix time (a dispatcher), under the problems: who does, and what they can do.
 * A missing clock-out is worth a call; anything else waits for a manager. Null without problems.
 */
export function problemsNoteWords(problems: readonly TimeProblem[], first: string): string | null {
  if (!problems.length) return null;
  return problems.some((p) => p.kind === 'noClockOut')
    ? `A manager fixes this. You can call ${first}.`
    : `${first}’s time needs a manager.`;
}

/** Each clause stays on one line, so a long line breaks only at its commas. */
function Clauses({ words }: { words: string }) {
  const parts = words.split(', ');
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {i ? ' ' : ''}
          <span className="fl-techday__clause">
            {p}
            {i < parts.length - 1 ? ',' : ''}
          </span>
        </Fragment>
      ))}
    </>
  );
}

function nowWords(now: TechNow): string {
  switch (now.kind) {
    case 'working':
      return `Working since ${clockWord(now.since)}`;
    case 'driving':
      return `Driving since ${clockWord(now.since)}`;
    case 'nonJob':
      return `${now.words} since ${clockWord(now.since)}`;
    case 'notClockedIn':
      return 'Not on the clock yet';
    case 'ended':
      return `Day ended ${clockWord(now.at)}`;
  }
}

/** Where they're working: the visit's site, on its own line; pressing it selects that visit. */
function Site({ now, onSelectVisit }: { now: TechNow; onSelectVisit?: (id: string) => void }) {
  if (now.kind !== 'working') return null;
  const id = now.visitId;
  return (
    <p className="fl-techday__line fl-techday__site">
      {id && onSelectVisit ? (
        <button
          type="button"
          className="fl-techday__address"
          aria-label={`At ${now.address}: show this visit on the board`}
          onClick={() => onSelectVisit(id)}
        >
          {now.address}
        </button>
      ) : (
        <span className="fl-techday__address" data-plain="">
          {now.address}
        </span>
      )}
    </p>
  );
}

export function TechDayCard({
  day,
  asOf,
  status = 'live',
  detail = 'full',
  showProblems = true,
  problems: problemWords,
  problemsNote,
  overtimeAfter = 480,
  onSelectVisit,
  onRetry,
}: TechDayCardProps) {
  const head = (
    <div className="fl-techday__head">
      <span className="fl-techday__name">
        <PenKey pen={day.pen}>{day.name}</PenKey>
      </span>
      {status === 'live' || status === 'offline' ? (
        <span className="fl-techday__asof">
          as of {clockWord(asOf)}
          {status === 'offline' ? ', offline' : ''}
        </span>
      ) : null}
    </div>
  );
  if (status === 'loading')
    return (
      <div className="fl-techday" data-status="loading" aria-busy="true">
        {head}
        <p className="fl-techday__line" data-muted="">
          Getting their day
        </p>
      </div>
    );
  if (status === 'failed')
    return (
      <div className="fl-techday" data-status="failed">
        {head}
        <p className="fl-techday__line" data-muted="">
          Their day didn’t load.{' '}
          {onRetry ? (
            <button type="button" className="fl-techday__retry" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </p>
      </div>
    );

  const paid = day.totals.work + day.totals.drive + day.totals.nonJob;
  const full = detail === 'full';
  const onClock = day.clockIn !== null && day.now.kind !== 'notClockedIn';
  const today =
    full && onClock && day.clockIn !== null
      ? day.now.kind === 'ended'
        ? `Clocked in ${clockWord(day.clockIn)}, ${minutesWords(paid)} in all`
        : `On the clock since ${clockWord(day.clockIn)}, ${minutesWords(paid)} so far`
      : null;
  const split = today
    ? `Work ${minutesWords(day.totals.work)}, drive ${minutesWords(day.totals.drive)}, shop ${minutesWords(day.totals.nonJob)}`
    : null;
  const overtime = full ? overtimeAhead(day, asOf, overtimeAfter) : null;
  const problems =
    full && showProblems
      ? problemWords
        ? [...problemWords]
        : [
            day.leftOpen === 1
              ? `Clock-out missing for ${day.leftOpenOn ?? 'an earlier day'}`
              : day.leftOpen > 1
                ? `${day.leftOpen} clock-outs missing, the newest ${day.leftOpenOn ?? 'on an earlier day'}`
                : null,
            day.flagged ? `${count(day.flagged, 'entry', 'entries')} flagged for payroll` : null,
          ].filter((p): p is string => p !== null)
      : [];
  const note = problems.length && problemsNote ? problemsNote : null;
  const truck = full ? truckWords(day.truck) : null;

  return (
    <div className="fl-techday" data-status={status}>
      {head}
      <div className="fl-techday__group">
        <p className="fl-techday__line fl-techday__now">{nowWords(day.now)}</p>
        <Site now={day.now} onSelectVisit={status === 'live' ? onSelectVisit : undefined} />
      </div>
      {today ? (
        <div className="fl-techday__group">
          <p className="fl-techday__line">
            <Clauses words={today} />
          </p>
          <p className="fl-techday__line" data-muted="">
            {split ? <Clauses words={split} /> : null}
          </p>
        </div>
      ) : null}
      {overtime ? (
        <p className="fl-techday__line" data-bad={overtime.bad ? '' : undefined}>
          <Clauses words={overtime.words} />
        </p>
      ) : null}
      {problems.length ? (
        <div className="fl-techday__group">
          {/* the red rule runs beside the problems only; who fixes them is plain, under it */}
          <div className="fl-techday__group" data-bad="">
            {problems.map((p) => (
              <p key={p} className="fl-techday__line">
                {p}
              </p>
            ))}
          </div>
          {note ? <p className="fl-techday__line fl-techday__note">{note}</p> : null}
        </div>
      ) : null}
      {truck ? (
        <p className="fl-techday__line">
          <Clauses words={truck} />
        </p>
      ) : null}
    </div>
  );
}
